import { FC, useRef, useMemo, useEffect, } from "react";
// 组件
import { PlusOutlined } from "@ant-design/icons";
import { Table } from "antd";
import { ProTable, ProForm, } from '../antd';
import TableRow from "./subpage/row";
import TableCell from "./subpage/cell";
import TotalTable, { TotalTableActionType } from "./subpage/totalTable";
// 方法
import classNames from "classnames";
import MianSubTableContext from './context/mianSubTableContext';
import useEditSubTablehook from './hook/useEditSubTablehook';
import columnsHook from './hook/columnsHook';
import { filterDeleteData, formatChangeInfo, formatRowKey, getEffectiveChildren, } from "./util";
import { useLayoutContext } from "../PageLayout/context/LayoutContext";
import { getRecordCode } from "../EdiTableCopy/utils";
// 常量
import { IObject } from "../constants/interface";
import { EditMainSubTablePrope, MAIN_ROW_KEY, mainFormKey, } from "./interface";
import { STATUS_CREATED_CODE, STATUS_CREATED } from "../constants/constant";
import './style.less';
/** 可编辑主子表 */
const EditMainSubTableNew: FC<EditMainSubTablePrope> = props => {
  const {
    height, dataSource, defaultExpandAllRows, className, bordered, disabled, style, showAddSubRow, showEmptySubRow,
    actionRef, totalKeys, totalIndex, totalText, onValuesChange, hiddenAddSubRow, formatTotalData,
  } = props;
  const { enumData } = useLayoutContext();
  const tableRef = useRef<HTMLDivElement>(null);
  const flagIndex = useRef<number>(0);
  const totalTableRef = useRef<TotalTableActionType>();
  const [form] = ProForm.useForm();
  const { tableDataSource, expandedRowKeys, dispatchEditSubTable, } = useEditSubTablehook(flagIndex, dataSource, form, defaultExpandAllRows, showAddSubRow, showEmptySubRow);
  const { tableColumns } = columnsHook(props, dispatchEditSubTable, tableDataSource, enumData, form,);
  /** 可编辑主子表格展示的数据 */
  const editMainSubTableDataSource = useMemo(() => {
    const newDataSource = filterDeleteData(tableDataSource, hiddenAddSubRow);
    return newDataSource;
  }, [tableDataSource]);
  useEffect(() => {
    handleUpdateTotalData();
  }, [tableDataSource]);
  /** 表单字段值改变 */
  const handleValuesChange = (changedValues: any, values: any) => {
    const changeInfo = formatChangeInfo(changedValues);
    const { name, value, mainKey, childrenKey, } = changeInfo;
    // 是否需要更新总计
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const findItem = totalKeys.find(v => v.dataIndex === changeInfo.name);
      if (findItem) {
        handleUpdateTotalData();
      }
    }
    form.setFieldValue([mainKey, childrenKey, STATUS_CREATED_CODE], STATUS_CREATED.UPDATE);
    if (childrenKey !== mainFormKey) {
      form.setFieldValue([mainKey, mainFormKey, STATUS_CREATED_CODE], STATUS_CREATED.UPDATE);
    }
    onValuesChange?.(name, value, mainKey, childrenKey, form, values[childrenKey as string]);
  };
  /** 主表展开、合并 */
  const handleExpand = (expanded: boolean, record: any) => {
    if (expanded) {
      dispatchEditSubTable('addExpandedRowKeys', {updateExpandedRowKeys: record.key});
    } else {
      dispatchEditSubTable('deleteExpandedRowKeys', {updateExpandedRowKeys: record.key});
    }
  };
  /** 添加主表行 */
  const handleMainAdd = (addItem?: IObject | IObject[]) => {
    dispatchEditSubTable('addRow', {addItem});
  };
  /** 添加子表行 */
  const handleChildrenAdd = (mainKey: string, addItem?: IObject | IObject[]) => {
    dispatchEditSubTable('addChildren', { mainKey, addItem });
  };
  /** 追加主子表数据 */
  const handleAppend = (appendItem: IObject[]) => {
    dispatchEditSubTable('append', {appendItem});
  }
  /** 覆盖主子表数据 */
  const handleCover = (addItem?: IObject[]) => {
    dispatchEditSubTable('cover', {addItem});
  }
  /** 删除 */
  const handleDelete = (record: any) => {
    const { mainKey, childrenKey } = formatRowKey(record.key);
    if (mainKey && childrenKey) {
      dispatchEditSubTable('deleteChildren', {mainKey, childrenKey: record.key});
      return;
    }
    if (mainKey && !childrenKey) {
      dispatchEditSubTable('deleteRow', {mainKey});
    }
  };
  /** 提交 */
  const handleSubmit = async () => {
    try {
      const formData = await form.validateFields();
      if (Array.isArray(tableDataSource) && tableDataSource.length) {
        return tableDataSource.map(group => {
          const { children, key: groupKey, [MAIN_ROW_KEY]: isMainTable, [STATUS_CREATED_CODE]: groupRecordStatus, ...restGroup } = group;
          const rowFormData = formData[groupKey] ?? {};
          const mainFormData = rowFormData.main ?? {};
          const groupData: any = {
            ...restGroup,
            ...mainFormData,
            [STATUS_CREATED_CODE]: getRecordCode(
              groupRecordStatus,
              mainFormData[STATUS_CREATED_CODE],
            ),
          };
          const groupChildren = getEffectiveChildren(children);
          if (Array.isArray(groupChildren) && groupChildren.length) {
            const childrenData: any[] = [];
            groupChildren.forEach(item => {
              const { key: itemKey, [STATUS_CREATED_CODE]: itemRecordStatus, ...restItem } = item;
              const childrenKey = formatRowKey(itemKey).childrenKey;
              const childrenFormData = childrenKey ? (rowFormData[childrenKey] ?? {}) : {};
              childrenData.push({
                ...restItem,
                ...childrenFormData,
                [STATUS_CREATED_CODE]: getRecordCode(
                  itemRecordStatus,
                  childrenFormData[STATUS_CREATED_CODE],
                ),
              });
            });
            groupData.children = childrenData;
          }
          return groupData;
        });
      }
      return [];
    } catch (errorInfo) {
      return false;
    }
  };
  /** 清空 */
  const handleEmptyMainChildren = (mainKey: string) => {
    dispatchEditSubTable('emptyRow', {mainKey});
  };
  /** 清空 */
  const handleEmpty = () => {
    dispatchEditSubTable('empty');
  };
  /** 替换 */
  const handleChangeDataSource = (data: any[]) => {
    dispatchEditSubTable('changeData', { dataSource: data, });
  };
  /**
   * 返回dataSource
   * @param isFilterDeleteData 是否过滤已删除数据
   * @returns 
   */
  const handleGetDataSource = (isFilterDeleteData?: boolean) => {
    const newDataSource: any[] = [];
    const formData = form.getFieldsValue(true);
    if (Array.isArray(tableDataSource) && tableDataSource.length) {
      tableDataSource.forEach(group => {
        const { children, key: groupKey, [STATUS_CREATED_CODE]: groupRecordStatus, ...restGroup } = group;
        if (isFilterDeleteData && groupRecordStatus === STATUS_CREATED.DELETE) {
          return;
        }
        const rowFormData = formData[groupKey] ?? {};
        const mainFormData = rowFormData.main ?? {};
        const groupData: any = {
          ...restGroup,
          ...mainFormData,
          key: groupKey,
          [STATUS_CREATED_CODE]: getRecordCode(
            groupRecordStatus,
            mainFormData[STATUS_CREATED_CODE],
          ),
        };
        const groupChildren = getEffectiveChildren(children);
        if (Array.isArray(groupChildren) && groupChildren.length) {
          const childrenData: any[] = [];
          groupChildren.forEach(item => {
            const { key: itemKey, [STATUS_CREATED_CODE]: itemRecordStatus, ...restItem } = item;
            if (isFilterDeleteData && itemRecordStatus === STATUS_CREATED.DELETE) {
              return;
            }
            const childrenKey = formatRowKey(itemKey).childrenKey;
            const childrenFormData = childrenKey ? (rowFormData[childrenKey] ?? {}) : {};

            childrenData.push({
              ...restItem,
              ...childrenFormData,
              key: itemKey,
              [STATUS_CREATED_CODE]: getRecordCode(
                itemRecordStatus,
                childrenFormData[STATUS_CREATED_CODE],
              ),
            });
          });
          groupData.children = childrenData;
        }
        newDataSource.push(groupData);
      });
    }
    return newDataSource;
  };
  /** 更新统计数据 */
  const handleUpdateTotalData = () => {
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const effectiveDataSource = handleGetDataSource(true);
      totalTableRef.current?.onChange(effectiveDataSource);
    }
  };
  /** 手动传入表格数据进行统计数据更新 */
  const handleChangeTotal = (tableData?: any[]) => {
    if (tableData) {
      totalTableRef.current?.onChange(tableData);
    } else {
      handleUpdateTotalData();
    }
  };
  /** 更新单行主表表单数据 */
  const handleMainValues = (mainKey: string, values: any) => {
    if (mainKey === undefined) return;
    const mainName = [mainKey, mainFormKey];
    const formData = form.getFieldValue(mainName);
    form.setFieldValue(mainName, {
      ...formData,
      ...values,
      [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE,
    });
  };
  /** 更新单行主表下子表的表单数据 */
  const handleMainChildrenValues = (childrenKey: string, values: any) => {
    if (childrenKey === undefined) return;
    const recordKey = formatRowKey(childrenKey);
    const childrenName = [recordKey.mainKey, recordKey.childrenKey];
    const formData = form.getFieldValue(childrenName);
    form.setFieldValue(childrenName, {
      ...formData,
      ...values,
      [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE,
    });
  };
  /** 更新单行主表下所有子表的表单数据 */
  const handleMainChildrensValues = (mainKey: string, values: any) => {
    if (mainKey === undefined) return;
    const childrenData = handleGetDataSource(true).find(item => item.key === mainKey)?.children;
    if (Array.isArray(childrenData) && childrenData.length) {
      const changeData: any = {};
      childrenData.forEach(item => {
        handleMainChildrenValues(item.key, values);
      });
    }
  };
  const classes = useMemo(() => {
    return classNames('edit-main-sub-table', className);
  }, [className]);
  if (actionRef) {
    actionRef.current = {
      form,
      empty: handleEmpty,
      cover: handleCover,
      append: handleAppend,
      delete: handleDelete,
      submit: handleSubmit,
      addMain: handleMainAdd,
      addChildren: handleChildrenAdd,
      changeTotal: handleChangeTotal,
      getDataSource: handleGetDataSource,
      setMainValues: handleMainValues,
      changeDataSource: handleChangeDataSource,
      emptyMainChildren: handleEmptyMainChildren,
      setMainChildrenValues: handleMainChildrenValues,
      setMainChildrensValues: handleMainChildrensValues,
    };
  }
  return (
    <MianSubTableContext.Provider value={{
      dispatchEditSubTable,
      form,
      showAddSubRow,
      showEmptySubRow,
    }}>
      <div
        ref={tableRef}
        className={classes}
        style={style}
      >
        <ProForm
          component={false}
          form={form}
          name='edit-main-sub-table-form'
          onValuesChange={handleValuesChange}
        >
          <ProTable
            dataSource={editMainSubTableDataSource}
            columns={tableColumns}
            pagination={false}
            bordered={bordered}
            size="small"
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
            }}
            components={{
              body: {
                row: TableRow,
                cell: TableCell,
              },
            }}
            scroll={{
              scrollToFirstRowOnChange: true,
              y: height,
            }}
            // @ts-ignore
            onRow={() => ({
              disabled,
            })}
            summary={() => {
              if (
                Array.isArray(totalKeys) &&
                !!totalKeys.length &&
                Array.isArray(editMainSubTableDataSource) &&
                !!editMainSubTableDataSource.length
              ) {
                return (
                  <Table.Summary fixed>
                    <TotalTable
                      actionRef={totalTableRef}
                      columns={tableColumns}
                      totalKeys={totalKeys}
                      totalIndex={totalIndex}
                      totalText={totalText}
                      formatTotalData={formatTotalData}
                    />
                  </Table.Summary>
                );
              }
            }}
          />
        </ProForm>
      </div>
    </MianSubTableContext.Provider>
  );
};

EditMainSubTableNew.defaultProps = {
  height: 350,
  defaultExpandAllRows: true,
  deleteType: 'ALL',
  addIcon: <PlusOutlined />,
  hideAdd: false,
  disabled: false,
  bordered: true,
  showAddSubRow: false,
  addSubRowText: '添加一行',
  showEmptySubRow: true,
  emptySubRowText: '暂无数据',
  operationWidth: 50,
};

export default EditMainSubTableNew;
